import { useQuery } from '@apollo/client'
import {
  DatabaseArrowDown,
  ChartLine,
  CloudUpload,
  History,
  Scan,
  Settings,
  Lock,
  DatabaseBackup,
  DatabaseZap,
  Waypoints,
} from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/sv/sidebar'
import { useCompanyPath, useIdCompany, useRBAC } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { DATABASE_DISPLAY_NAME_QUERY } from '@/routes/database/queries'
import { isOciDumpBackupDatabase } from '@/routes/database/tabs/backups/utils'
import type {
  DatabaseDisplayNameQuery,
  DatabaseDisplayNameQueryVariables,
} from '@/routes/database/queries.types.gen'

const items = [
  {
    title: 'Overview',
    url: '/database/:databaseName/overview',
    icon: Scan,
    activeTags: ['overview'],
  },
  {
    title: 'Studio',
    url: '/database/:databaseName/studio',
    icon: DatabaseZap,
    activeTags: ['studio'],
  },
  {
    title: 'Analytics',
    url: '/database/:databaseName/analytics',
    icon: ChartLine,
    activeTags: ['analytics'],
  },
  {
    title: 'Backup management',
    url: '/database/:databaseName/backups',
    icon: DatabaseBackup,
    activeTags: ['backups'],
  },
  {
    title: 'Networking',
    url: '/database/:databaseName/networking',
    icon: Waypoints,
    activeTags: ['networking'],
  },
  {
    title: 'Settings',
    url: '/database/:databaseName/settings',
    icon: Settings,
    activeTags: ['/settings'],
  },
] as const

export const DatabaseNav = () => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const location = useLocation()
  const { getCompanyPath } = useCompanyPath()
  const { databaseName = '' } = useParams<{ databaseName: string }>()
  const { data } = useQuery<
    DatabaseDisplayNameQuery,
    DatabaseDisplayNameQueryVariables
  >(DATABASE_DISPLAY_NAME_QUERY, {
    variables: { databaseName },
    fetchPolicy: 'cache-only',
  })
  const idDatabase = useMemo(() => data?.database?.id ?? '', [data])
  const idProjectDatabase = useMemo(
    () => data?.database?.idProject ?? '',
    [data]
  )

  const database = data?.database
  const hasLegacyBackups =
    data === undefined ? true : !isOciDumpBackupDatabase(database)
  const hasExternalBackups = isOciDumpBackupDatabase(database)

  const isExternalBackupsActive =
    location.pathname.includes('/backups/external')
  const isRestoreHistoryActive = location.pathname.includes('/backups/history')
  const isLegacyBackupsActive =
    location.pathname.includes('/backups') &&
    !isExternalBackupsActive &&
    !isRestoreHistoryActive

  const backupsSubItems = useMemo(
    () => [
      ...(hasLegacyBackups
        ? [
            {
              title: 'Backups',
              url: '/database/:databaseName/backups' as const,
              icon: DatabaseArrowDown,
              isActive: isLegacyBackupsActive,
            },
          ]
        : []),
      ...(hasExternalBackups
        ? [
            {
              title: 'External backups',
              url: '/database/:databaseName/backups/external' as const,
              icon: CloudUpload,
              isActive: isExternalBackupsActive,
            },
          ]
        : []),
      {
        title: 'Restore history',
        url: '/database/:databaseName/backups/history' as const,
        icon: History,
        isActive: isRestoreHistoryActive,
      },
    ],
    [
      hasLegacyBackups,
      hasExternalBackups,
      isLegacyBackupsActive,
      isExternalBackupsActive,
      isRestoreHistoryActive,
    ]
  )
  const hasAccess = useMemo(
    () =>
      data === undefined
        ? true
        : rbac
            .company(idCompany)
            .project(idProjectDatabase)
            .database(idDatabase)
            .view.can(),
    [rbac, idCompany, idDatabase, idProjectDatabase, data]
  )

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const isActive = item.activeTags.some((tag) =>
              location.pathname.includes(tag)
            )
            const isBackupsSubItemActive = backupsSubItems.some(
              (subItem) => subItem.isActive
            )
            return (
              <SidebarMenuItem
                key={item.url}
                title={item.title}
                className={cn(
                  !hasAccess
                    ? 'opacity-50 cursor-not-allowed pointer-events-none'
                    : undefined
                )}
              >
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  disabled={!hasAccess}
                  className={cn(
                    item.title === 'Backup management' &&
                      isBackupsSubItemActive &&
                      '[&_>_svg]:text-sidebar-accent-foreground'
                  )}
                >
                  {hasAccess ? (
                    <Link
                      to={getCompanyPath({
                        pathname: item.url,
                        variables: { databaseName },
                      })}
                    >
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Lock />
                      <span>{item.title}</span>
                    </div>
                  )}
                </SidebarMenuButton>
                {item.title === 'Backup management' && hasAccess ? (
                  <SidebarMenuSub>
                    {backupsSubItems.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.url}>
                        <SidebarMenuSubButton
                          asChild
                          isActive={subItem.isActive}
                        >
                          <Link
                            to={getCompanyPath({
                              pathname: subItem.url,
                              variables: { databaseName },
                            })}
                          >
                            <subItem.icon />
                            <span>{subItem.title}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                ) : null}
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
